import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { useTheme as useNextTheme } from "next-themes";
import PropTypes from "prop-types";
import React, { useCallback, useEffect } from "react";

const logger = appLogger.withTag("StickyCard");

declare const stickyio: {
  init: (appId: string, config: { customCSS: string; cleanExistingCSS: boolean }) => void;
  tokenizeCard: () => void;
  onTokenSuccess: (token: string) => void;
  onTokenError: (errors: unknown) => void;
  onCardValidation: (valid: boolean) => void;
};

const APP_ID = "nextdayai";

const COMMON_CSS = `
  .card-wrapper {
    box-shadow: none;
  }
  .card-wrapper input {
    font-family: "-apple-system", "BlinkMacSystemFont", 'Segoe UI','Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans','Helvetica Neue', "sans-serif";
    border: none;
  }
  .card-wrapper input::placeholder:focus{
    color: transparent;
  }
  #stickyio_cc_number {
    padding-left: 55px !important;
  }
`;

const DARK_CSS = `
  body, .card-wrapper, .card-container {
    background: #27272A; 
  }
  .footer {
    color: #27272A; /* Change to your desired text color */
  }
  .card-wrapper input {
    background: #18181B;
    color: #FFFFFF;
  }
  .card-wrapper input::placeholder{
    color: rgb(250 250 250/0.7);
  }
  ${COMMON_CSS}
`;

const LIGHT_CSS = `
  body {
    background-color: white;
  }
  .card-wrapper {
    background: #F4F4F5;
  }
  .footer {
    color: #F4F4F5; /* Change to your desired text color */
  }
  ${COMMON_CSS}
`;

const StickyCard = ({
  setIsCardValid,
  setCreditCardToken,
}: {
  setIsCardValid: (valid: boolean) => void;
  setCreditCardToken: (token: string) => void;
}) => {
  const { theme } = useNextTheme();

  const handleTokenError = useCallback((errors: unknown) => {
    logger.error("Received an error: ", errors);
  }, []);

  const handleTokenSuccess = useCallback(
    (token: string) => {
      setCreditCardToken(token);
    },
    [setCreditCardToken],
  );

  useEffect(() => {
    // Load the external script
    const script = document.createElement("script");
    script.src = "https://cdn.sticky.io/jssdk-v2/stickyio-sdk.js";
    script.onload = () => {
      if (document.getElementsByClassName("stickyio-iframe").length === 0) {
        //we resize the iframe to 120x height
        const style = document.createElement("style");
        style.innerHTML = ".stickyio-iframe { height: 120px !important;}";
        document.head.appendChild(style);

        stickyio.init(APP_ID, {
          customCSS: theme === "dark" ? DARK_CSS : LIGHT_CSS,
          cleanExistingCSS: false,
        });

        stickyio.onTokenSuccess = function (token) {
          handleTokenSuccess(token);
        };

        stickyio.onTokenError = function (errors) {
          handleTokenError(errors);
        };

        stickyio.onCardValidation = function (valid) {
          setIsCardValid(valid);
          if (!valid) {
            // @ts-expect-error - setCreditCardToken expects a string, but we are setting it to null
            setCreditCardToken(null);
          } else {
            stickyio.tokenizeCard();
          }
        };
      }
    };
    document.body.appendChild(script);

    // Clean up the script if the component is unmounted
    return () => {
      document.body.removeChild(script);
      // Remove the iframe(s)
      const iframesToRemove = document.getElementsByClassName("stickyio-iframe");
      while (iframesToRemove.length > 0) {
        if (iframesToRemove[0]?.parentNode) iframesToRemove[0]?.parentNode?.removeChild(iframesToRemove[0]);
      }
    };
  }, [theme, setIsCardValid, setCreditCardToken, handleTokenError, handleTokenSuccess]);

  return (
    <>
      <div className="bg-default-100 rounded-lg shadow-md p-1 mb-2">
        <div id="stickyio_card" className="max-w-[400px]"></div>
      </div>
    </>
  );
};

StickyCard.propTypes = {
  setIsCardValid: PropTypes.func.isRequired,
  setCreditCardToken: PropTypes.func.isRequired,
};

export default StickyCard;
